import React, { useEffect } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import CustomTable, { renderBooleanCell } from '../misc/CustomTable';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectAgreementsData } from '../../store/general/general-slice-selectors';
import Stack from '@mui/material/Stack';
import { AgreementType } from '../../models/enums/agreements';
import { getAgreements } from '../../store/general/general-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { dateToStringFormat } from '@common/utils/date';
import { setSelectedAgreementId } from '../../store/general/general-slice';

const renderOrder = (params: GridRenderCellParams) => {
  return params.value + 1;
};

const renderValidFrom = (params: GridRenderCellParams) => {
  return dateToStringFormat(params.value);
};

const AgreementsTable: React.FC<{ agreementType: AgreementType }> = ({ agreementType }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));

  useEffect(() => {
    if (agreementsData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getAgreements());
    }
  }, [agreementsData.rest.getStatus, dispatch]);

  const rowClickHandler = (id: string) => {
    dispatch(setSelectedAgreementId(id));
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('table-header-name') as string, width: 250 },
    {
      field: 'order',
      headerName: t('table-header-agreement-order') as string,
      renderCell: renderOrder,
      width: 150,
    },
    {
      field: agreementType === AgreementType.ORGANIZATION ? 'organizations' : 'users',
      headerName:
        agreementType === AgreementType.ORGANIZATION
          ? t('table-header-organization-count')
          : (t('table-header-user-count') as string),
      width: 100,
    },
    {
      field: 'validFrom',
      headerName: t('table-header-agreement-valid-from') as string,
      width: 100,
      renderCell: renderValidFrom,
    },
    {
      field: 'enabled',
      headerName: t('table-header-agreement-enabled') as string,
      renderCell: renderBooleanCell,
      width: 100,
    },
  ];

  const data = (agreementsData.data ?? []).filter((a) => a.agreementType === agreementType);

  return (
    <React.Fragment>
      <Stack direction="row" alignItems="center" spacing={1}></Stack>
      <CustomTable
        getStatus={agreementsData.rest.getStatus}
        loadingMessage={t('loading-agreements')}
        dataLoaded={agreementsData.data != null}
        failedToLoadMessage={agreementsData.rest.getError?.message ?? t('error-loading-agreements')}
        data={data}
        emptyDataMessage={t('info-no-agreements-' + agreementType)}
        columns={columns}
        rowClickHandler={rowClickHandler}
      />
    </React.Fragment>
  );
};

export default AgreementsTable;
